/**
 * Dependencies.
 */
import { useState, useId, useMemo } from "react";
import { createElement as el } from "@wordpress/element";
import Tab from "../../interfaces/iTab";
import "./tabPanel.css";

interface TabPanelProps {
  readonly name: string;
  readonly className: string;
  readonly activeClass: string;
  readonly tabs: Array<Tab>;
  readonly initialTabName: string;
  children?: any;
  orientation?: string;
}

/**
 * Provides a TabPanel.
 *
 * @see EXPORTS in wp-includes\js\dist\components.js
 *
 * @param {TabPanelProps} props Initial data.
 * @return {element}            TabPanel.
 */
const TabPanel = (props: TabPanelProps) => {
  const {
    tabs,
    initialTabName,
    activeClass,
    children,
    ...values
  }: TabPanelProps = props;

  const prefix: string = "tab-panel";
  const instanceId = prefix + useId();
  const [selectedTabName, setSelected] = useState(initialTabName);
  // console.log("Tab panel init :: ", initialTabName);

  const selectedTab = useMemo(() => {
    const getSelectedTab = (tabName: string) => {
      var tab: Tab = {
        name: "",
        title: "",
        className: "",
        callback: () => {},
      };
      tabs.forEach((_tab: Tab) => {
        if (_tab.name === tabName) {
          tab = _tab;
        }
      });
      // console.log("useMemo selectedTab -> selectedTabName ::", selectedTabName);
      return tab;
    };
    return getSelectedTab(selectedTabName);
  }, [selectedTabName, tabs]);

  const selectedId = useMemo(() => {
    const getId = (id: string, name: string) => {
      // console.log("useMemo ID  -> selectedId ::", `${id}-${name}`);
      return `${id}-${name}`;
    };
    return getId(instanceId, selectedTabName);
  }, [instanceId, selectedTabName]);

  const classNamesDefault = (tab: Tab) => {
    return (
      "components-tab-panel__tabs-item " +
      (tab.name === selectedTabName ? activeClass : "")
    );
  };

  const handleClick = (tabName: string) => {
    // console.log("New tabName :: ", tabName);
    setSelected(tabName);
  };

  return el(
    "div",
    {
      className: values.className,
    },
    el(
      "div",
      {
        role: "tablist",
        orientation: values.orientation || "horizontal",
        className: "components-tab-panel__tabs",
      },
      tabs.map((tab: Tab) =>
        el(
          "div",
          {
            className: classNamesDefault(tab),
            tabid: `${instanceId}-${tab.name}`,
            "aria-controls": `${instanceId}-${tab.name}-view`,
            selected: tab.name === selectedTabName,
            key: tab.name,
            onClick: () => handleClick(tab.name),
          },
          tab.title
        )
      )
    ),
    selectedTab &&
      el(
        "div",
        {
          key: selectedId,
          "aria-labelledby": selectedId,
          role: "tabpanel",
          id: `${selectedId}-view`,
          className: "components-tab-panel__tab-content",
        },
        children(selectedTab)
      )
  );
};

export default TabPanel;
